import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, CircularProgress, MenuItem, Select, Typography,
} from "@mui/material";
import {
  Bar, BarChart, CartesianGrid, Cell, ComposedChart, Legend, Line,
  ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import {
  getDashboardCampaigns          as getCampaigns,
  getDashboardDelinquencyByDay   as getDelinqByDay,
  getDashboardDelinquencyByDpd   as getDelinqByDpd,
  getDashboardDelinquencyUnique  as getDelinqUnique,
} from "../../services/pbi/pbiservices";

// ── Palette ────────────────────────────────────────────────────────
const BLUE   = "#378ADD";
const GREEN  = "#1D9E75";
const AMBER  = "#BA7517";
const PINK   = "#D4537E";
const RED    = "#A32D2D";
const GREY   = "#888780";

const UPLOAD_C   = "#7F77DD";   // inflow (uploaded delinquent)
const RESOLVE_C  = GREEN;        // resolved (cured)
const RATE_C     = AMBER;        // resolution %

const GRAN_OPTIONS  = ["Day", "Week", "Month"];
const RANGE_OPTIONS = [
  "1D", "2D", "3D",
  "7D", "30D",
  "C Wk", "CW+Last",
  "This Mo", "Cur+Last",
];

const DPD_BANDS = ["1-9","10-19","20-29","30-39","40-49","50-59","60-89","90+"];

// ── Date helpers (shared shape with the other dashboards) ──────────
function toLocal(d) {
  const y  = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const dy = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${dy}`;
}

function getDateRange(preset) {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth();
  let start, end;

  const dow        = now.getDay();
  const thisMonday = new Date(now);
  thisMonday.setDate(now.getDate() - ((dow + 6) % 7));
  thisMonday.setHours(0, 0, 0, 0);
  const lastMonday = new Date(thisMonday);
  lastMonday.setDate(thisMonday.getDate() - 7);

  switch (preset) {
    case "1D":       start = new Date(now); start.setDate(now.getDate() - 1);   end = now; break;
    case "2D":       start = new Date(now); start.setDate(now.getDate() - 2);   end = now; break;
    case "3D":       start = new Date(now); start.setDate(now.getDate() - 3);   end = now; break;
    case "7D":       start = new Date(now); start.setDate(now.getDate() - 7);   end = now; break;
    case "30D":      start = new Date(now); start.setDate(now.getDate() - 30);  end = now; break;
    case "C Wk":     start = thisMonday; end = now; break;
    case "CW+Last":  start = lastMonday; end = now; break;
    case "This Mo":  start = new Date(y, m, 1);     end = now; break;
    case "Cur+Last": start = new Date(y, m - 1, 1); end = now; break;
    case "Prev 2Mo": start = new Date(y, m - 2, 1); end = new Date(y, m, 0); break;
    case "Prev 3Mo":
    default:         start = new Date(y, m - 3, 1); end = new Date(y, m, 0); break;
  }
  return { startDate: toLocal(start), endDate: toLocal(end) };
}

function getPrevDateRange(startDate, endDate) {
  const parse = (s) => {
    const mm = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return mm ? new Date(+mm[1], +mm[2] - 1, +mm[3]) : new Date(s);
  };
  const start = parse(startDate), end = parse(endDate);
  const durMs = end.getTime() - start.getTime(), dayMs = 86_400_000;
  const prevEnd   = new Date(start.getTime() - dayMs);
  const prevStart = new Date(prevEnd.getTime() - durMs);
  return { startDate: toLocal(prevStart), endDate: toLocal(prevEnd) };
}

// ── Format helpers ─────────────────────────────────────────────────
function safeNum(v) { const n = Number(v); return isNaN(n) ? 0 : n; }

function fmtCurrency(n) {
  const num = Number(n);
  if (!num || isNaN(num)) return "$0";
  if (Math.abs(num) >= 1_000_000) return `$${(num / 1_000_000).toFixed(2)}M`;
  if (Math.abs(num) >= 1000)      return `$${(num / 1000).toFixed(1)}K`;
  return `$${num.toFixed(0)}`;
}

function fmtInt(n) { return safeNum(n).toLocaleString(); }
function fmtPct(v, dec = 1) { const n = Number(v); return isNaN(n) ? "—" : `${n.toFixed(dec)}%`; }

// Parse the YYYY-MM-DD prefix into a LOCAL date (no UTC shift) and render
// short — same approach as the PTP dashboard's fmtDate.
function fmtDate(val) {
  if (!val) return "";
  const m = typeof val === "string" && val.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(val);
  if (isNaN(d)) return String(val);
  return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" });
}

function buildDelta(cur, prev, fmtVal) {
  const c = Number(cur), p = Number(prev);
  if (!isFinite(c) || !isFinite(p) || p === 0) return null;
  const pct = ((c - p) / p) * 100, up = pct > 0;
  return { up, good: up, text: `${up ? "+" : ""}${pct.toFixed(1)}% vs ${fmtVal ? fmtVal(p) : p.toLocaleString()}` };
}

// ── Sub-components ─────────────────────────────────────────────────
function SectionTitle({ children }) {
  return (
    <Typography sx={{
      fontSize: 11, fontWeight: 500, color: "text.secondary",
      textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
    }}>{children}</Typography>
  );
}

function KpiTile({ label, value, sub, color = BLUE, delta }) {
  return (
    <Box sx={{
      background: "var(--mui-palette-background-default, #f7f7f7)",
      borderRadius: 2, p: "10px 12px", minWidth: 0,
      display: "flex", flexDirection: "column", gap: "4px",
    }}>
      <Typography sx={{
        fontSize: 10, color: "text.secondary", textTransform: "uppercase",
        letterSpacing: "0.05em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>{label}</Typography>
      <Typography sx={{ fontSize: 20, fontWeight: 500, color, lineHeight: 1.2 }}>{value}</Typography>
      {sub != null && (
        <Typography sx={{ fontSize: 12, color: "text.secondary", lineHeight: 1.2 }}>{sub}</Typography>
      )}
      {delta && (
        <Typography sx={{ fontSize: 11, color: delta.good ? "#3B6D11" : "#A32D2D", mt: 0.25 }}>
          {delta.up ? "▲" : "▼"} {delta.text}
        </Typography>
      )}
    </Box>
  );
}

function ChartCard({ title, children, height = 220 }) {
  return (
    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, p: "12px 14px" }}>
      {title && (
        <Typography sx={{
          fontSize: 11, fontWeight: 500, color: "text.secondary",
          textTransform: "uppercase", letterSpacing: "0.05em", mb: 1,
        }}>{title}</Typography>
      )}
      <Box sx={{ width: "100%", height }}>{children}</Box>
    </Box>
  );
}

function PillGroup({ label, options, value, onChange, groups }) {
  const boundaries = new Set();
  if (groups) { let idx = 0; groups.forEach((sz, gi) => { if (gi < groups.length - 1) { idx += sz; boundaries.add(idx); } }); }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography sx={{ fontSize: 11, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
        {options.map((opt, i) => {
          const active = value === opt;
          return (
            <React.Fragment key={opt}>
              {boundaries.has(i) && <Box sx={{ width: "1px", height: 18, bgcolor: "divider", mx: 0.5, flexShrink: 0 }} />}
              <Box onClick={() => onChange(opt)} sx={{
                px: "10px", py: "3px", fontSize: 12, borderRadius: "20px", cursor: "pointer", userSelect: "none",
                border: "0.5px solid", borderColor: active ? BLUE : "divider",
                background: active ? BLUE : "transparent", color: active ? "#fff" : "text.secondary",
                fontWeight: active ? 500 : 400, transition: "all .15s", "&:hover": { borderColor: BLUE },
              }}>{opt}</Box>
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
}

function Legendish({ items }) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 1, fontSize: 12, color: "text.secondary" }}>
      {items.map(it => (
        <Box key={it.label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: "2px", background: it.color }} />
          {it.label}
        </Box>
      ))}
    </Box>
  );
}

// ── Window totals from a by-day result set ─────────────────────────
function sumWindow(rows) {
  return (rows || []).reduce((a, r) => ({
    uploaded_count:  a.uploaded_count  + safeNum(r.uploaded_count),
    uploaded_amount: a.uploaded_amount + safeNum(r.uploaded_amount),
    resolved_count:  a.resolved_count  + safeNum(r.resolved_count),
    resolved_amount: a.resolved_amount + safeNum(r.resolved_amount),
  }), { uploaded_count: 0, uploaded_amount: 0, resolved_count: 0, resolved_amount: 0 });
}

// ── Main Dashboard ─────────────────────────────────────────────────
export default function DelinquenciesDashboard() {
  const [gran,        setGran]        = useState("Day");
  const [rangePreset, setRangePreset] = useState("30D");
  const [metric,      setMetric]      = useState("Count");   // Count | Dollars
  const [campaign,    setCampaign]    = useState("All");
  const [campaigns,   setCampaigns]   = useState(["All"]);

  const [byDay,     setByDay]     = useState([]);
  const [prevByDay, setPrevByDay] = useState([]);
  const [byDpd,     setByDpd]     = useState([]);
  const [uniq,      setUniq]      = useState({});
  const [prevUniq,  setPrevUniq]  = useState({});
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState("");

  const isDollars = metric === "Dollars";

  useEffect(() => {
    getCampaigns()
      .then(d => setCampaigns(["All", ...(d || []).map(c => c.name)]))
      .catch(() => {});
  }, []);

  const params = useMemo(() => ({
    ...getDateRange(rangePreset),
    campaign: campaign === "All" ? undefined : campaign,
    granularity: gran.toLowerCase(),
  }), [rangePreset, campaign, gran]);

  const prevParams = useMemo(() => ({
    ...getPrevDateRange(params.startDate, params.endDate),
    campaign: campaign === "All" ? undefined : campaign,
    granularity: gran.toLowerCase(),
  }), [params.startDate, params.endDate, campaign, gran]);

  const loadAll = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [day, prev, dpd, u, pu] = await Promise.all([
        getDelinqByDay(params),
        getDelinqByDay(prevParams).catch(() => []),
        getDelinqByDpd(params),
        getDelinqUnique(params).catch(() => ({})),
        getDelinqUnique(prevParams).catch(() => ({})),
      ]);
      setByDay(day   || []);
      setPrevByDay(prev || []);
      setByDpd(dpd   || []);
      setUniq(u      || {});
      setPrevUniq(pu || {});
    } catch (e) {
      console.error("Delinquency load error:", e?.response?.status, e?.message);
      setError(`Failed to load inflow data: ${e?.response?.data?.error || e?.message || "unknown"}`);
    } finally {
      setLoading(false);
    }
  }, [params, prevParams]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── KPI window totals + deltas ──
  // Uploaded uses UNIQUE customers across the window (deduped across days),
  // not the by-day sum (which double-counts customers seen in >1 upload).
  // Resolved comes from the by-day sum (each cure is a single event).
  // Resolution rate = resolved ÷ unique delinquent.
  const k = useMemo(() => {
    const curDay  = sumWindow(byDay);
    const prevDay = sumWindow(prevByDay);

    const upCount  = safeNum(uniq.unique_count);
    const upAmt    = safeNum(uniq.unique_amount);
    const pUpCount = safeNum(prevUniq.unique_count);
    const pUpAmt   = safeNum(prevUniq.unique_amount);

    const resCount  = curDay.resolved_count;
    const resAmt    = curDay.resolved_amount;
    const pResCount = prevDay.resolved_count;
    const pResAmt   = prevDay.resolved_amount;

    const rate    = upCount > 0 ? (resCount / upCount) * 100 : 0;
    const rateAmt = upAmt   > 0 ? (resAmt   / upAmt)   * 100 : 0;
    const pRate    = pUpCount > 0 ? (pResCount / pUpCount) * 100 : 0;
    const pRateAmt = pUpAmt   > 0 ? (pResAmt   / pUpAmt)   * 100 : 0;

    return {
      upCount, upAmt, resCount, resAmt, rate, rateAmt,
      upCountDelta:  buildDelta(upCount,  pUpCount,  fmtInt),
      upAmtDelta:    buildDelta(upAmt,    pUpAmt,    fmtCurrency),
      resCountDelta: buildDelta(resCount, pResCount, fmtInt),
      resAmtDelta:   buildDelta(resAmt,   pResAmt,   fmtCurrency),
      rateDelta:     buildDelta(rate,    pRate,    v => `${v.toFixed(1)}%`),
      rateAmtDelta:  buildDelta(rateAmt, pRateAmt, v => `${v.toFixed(1)}%`),
    };
  }, [byDay, prevByDay, uniq, prevUniq]);

  // ── By-day series: uploaded vs resolved + resolution % ──
  const dayData = useMemo(() => {
    const upK  = isDollars ? "uploaded_amount" : "uploaded_count";
    const resK = isDollars ? "resolved_amount" : "resolved_count";
    return (byDay || []).map(r => {
      const up  = safeNum(r[upK]);
      const res = safeNum(r[resK]);
      return {
        label: fmtDate(r.summary_date),
        uploaded: up,
        resolved: res,
        rate: up > 0 ? (res / up) * 100 : 0,
      };
    });
  }, [byDay, isDollars]);

  // ── Net flow per day (uploaded − resolved) ──
  const netData = useMemo(() => {
    const upK  = isDollars ? "uploaded_amount" : "uploaded_count";
    const resK = isDollars ? "resolved_amount" : "resolved_count";
    return (byDay || []).map(r => {
      const net = safeNum(r[upK]) - safeNum(r[resK]);
      return { label: fmtDate(r.summary_date), net };
    });
  }, [byDay, isDollars]);

  // ── By-DPD: uploaded vs resolved per band ──
  const dpdData = useMemo(() => {
    const upK  = isDollars ? "uploaded_amount" : "uploaded_count";
    const resK = isDollars ? "resolved_amount" : "resolved_count";
    const byBand = Object.fromEntries((byDpd || []).map(r => [r.dpd_range, r]));
    return DPD_BANDS.map(b => {
      const r = byBand[b] || {};
      return { dpd: b, uploaded: safeNum(r[upK]), resolved: safeNum(r[resK]) };
    });
  }, [byDpd, isDollars]);

  const fmtAxis = isDollars ? (v => fmtCurrency(v)) : (v => v);
  const fmtVal  = isDollars ? fmtCurrency : fmtInt;
  const axisStyle = { fontSize: 10, fill: "#888" };

  return (
    <Box sx={{ p: 2 }}>
      {/* ── Slicers ── */}
      <Box sx={{
        display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-end",
        pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
      }}>
        <PillGroup label="Granularity" options={GRAN_OPTIONS} value={gran} onChange={setGran} />
        <PillGroup label="Date range" options={RANGE_OPTIONS} value={rangePreset} onChange={setRangePreset} groups={[3, 2, 2, 2]} />
        <PillGroup label="Measure" options={["Count", "Dollars"]} value={metric} onChange={setMetric} />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignSelf: "flex-end" }}>
          <Typography sx={{ fontSize: 11, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Campaign
          </Typography>
          <Select value={campaign} onChange={e => setCampaign(e.target.value)} size="small" variant="outlined"
            sx={{
              fontSize: 12, borderRadius: "20px", minWidth: 120, height: 28,
              ".MuiOutlinedInput-notchedOutline": { borderColor: "divider", borderWidth: "0.5px" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BLUE },
              ".MuiSelect-select": { py: "3px", px: "10px", fontSize: 12, color: "text.secondary" },
              ".MuiSvgIcon-root": { fontSize: 16, color: "text.secondary" },
            }}>
            {campaigns.map(c => <MenuItem key={c} value={c} sx={{ fontSize: 12 }}>{c}</MenuItem>)}
          </Select>
        </Box>
      </Box>

      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 2 }}>
          <CircularProgress size={20} /> Loading inflow data…
        </Box>
      )}

      {!loading && !error && (
        <>
          {/* ── KPIs ── */}
          <SectionTitle>Inflow vs resolved (window totals)</SectionTitle>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 1, mb: 1.5 }}>
            <KpiTile label="Unique delinquent" value={fmtInt(k.upCount)}
              sub={fmtCurrency(k.upAmt) + " owed"} color={UPLOAD_C} delta={k.upCountDelta} />
            <KpiTile label="Resolved" value={fmtInt(k.resCount)}
              sub={fmtCurrency(k.resAmt) + " resolved"} color={RESOLVE_C} delta={k.resCountDelta} />
            <KpiTile label="Resolution rate" value={fmtPct(k.rate)}
              sub={fmtPct(k.rateAmt) + " by dollars"} color={AMBER} delta={k.rateDelta} />
          </Box>
          <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 1.5, mt: -0.5 }}>
            Unique delinquent counts each customer once across the window (deduped across upload days);
            the by-day chart below shows total uploaded per day, so a customer in multiple loads appears
            on each day.
          </Typography>

          {/* ── Inflow vs resolved by day ── */}
          <SectionTitle>Uploaded vs resolved — by {gran.toLowerCase()}</SectionTitle>
          <ChartCard title={`${isDollars ? "Dollars" : "Accounts"} uploaded vs resolved, with resolution %`} height={320}>
            <Legendish items={[
              { label: "Uploaded (delinquent)", color: UPLOAD_C },
              { label: "Resolved", color: RESOLVE_C },
              { label: "Resolution %", color: RATE_C },
            ]} />
            <ResponsiveContainer width="100%" height="85%">
              <ComposedChart data={dayData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                <XAxis dataKey="label" tick={axisStyle} interval="preserveStartEnd" />
                <YAxis yAxisId="left" tick={axisStyle} tickFormatter={fmtAxis} />
                <YAxis yAxisId="right" orientation="right" tick={{ ...axisStyle, fill: RATE_C }}
                  tickFormatter={v => `${v}%`} domain={[0, 100]} />
                <Tooltip contentStyle={{ fontSize: 12 }}
                  formatter={(v, n) => n === "Resolution %"
                    ? [`${safeNum(v).toFixed(1)}%`, n]
                    : [fmtVal(v), n]} />
                <Bar yAxisId="left" dataKey="uploaded" name="Uploaded (delinquent)" fill={UPLOAD_C} radius={[3,3,0,0]} />
                <Bar yAxisId="left" dataKey="resolved" name="Resolved"              fill={RESOLVE_C} radius={[3,3,0,0]} />
                <Line yAxisId="right" type="monotone" dataKey="rate" name="Resolution %"
                  stroke={RATE_C} strokeWidth={2} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>
          <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 1.5, mt: 0.75 }}>
            Resolution % is a throughput ratio (resolved that period ÷ uploaded that period) — uploaded and
            resolved are different customers, so it measures whether resolutions keep pace with intake, not a
            per-cohort cure rate.
          </Typography>

          {/* ── Net flow ── */}
          <SectionTitle>Net flow — uploaded minus resolved</SectionTitle>
          <ChartCard title={`Net change in delinquent ${isDollars ? "dollars" : "accounts"} (positive = book growing)`} height={240}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={netData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                <XAxis dataKey="label" tick={axisStyle} interval="preserveStartEnd" />
                <YAxis tick={axisStyle} tickFormatter={fmtAxis} />
                <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v) => [fmtVal(v), "Net flow"]} />
                <ReferenceLine y={0} stroke="#bbb" />
                <Bar dataKey="net" name="Net flow" radius={[3,3,0,0]}>
                  {netData.map((d, i) => (
                    <Cell key={i} fill={d.net >= 0 ? RED : GREEN} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* ── By DPD band ── */}
          <SectionTitle>Uploaded vs resolved — by DPD band</SectionTitle>
          <ChartCard title={`${isDollars ? "Dollars" : "Accounts"} by days-past-due`} height={340}>
            <Legendish items={[
              { label: "Uploaded (delinquent)", color: UPLOAD_C },
              { label: "Resolved", color: RESOLVE_C },
            ]} />
            <ResponsiveContainer width="100%" height="88%">
              <BarChart data={dpdData} layout="vertical" margin={{ top: 4, right: 16, bottom: 0, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" horizontal={false} />
                <XAxis type="number" tick={axisStyle} tickFormatter={fmtAxis} />
                <YAxis type="category" dataKey="dpd" tick={axisStyle} width={48} />
                <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v, n) => [fmtVal(v), n]} />
                <Bar dataKey="uploaded" name="Uploaded (delinquent)" fill={UPLOAD_C} radius={[0,3,3,0]} />
                <Bar dataKey="resolved" name="Resolved"              fill={RESOLVE_C} radius={[0,3,3,0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </>
      )}
    </Box>
  );
}